import * as ort from 'onnxruntime-web';
import { brandStore } from './brandStore.svelte';

const CLASSES = [
  'amortecedor', 'arrefecimento', 'direcao', 'eletrica',
  'embreagem', 'filtro', 'freio', 'iluminacao', 'motor', 'outro', 'suspensao'
] as const;

type ClassName = typeof CLASSES[number];

export interface InferenceResult {
  className: ClassName | null;
  confidence: number;
  allScores: Partial<Record<ClassName, number>>;
}

export interface LoadOptions {
  onProgress?: (progress: ProgressInfo) => void;
}

export interface ProgressInfo {
  loaded: number;
  total: number;
  percent: number;
  stage: string;
}

export interface DebouncedClassifier {
  processFrame(imageData: ImageData): void;
  reset(): void;
}

const MODEL_URL = '/model/yolo_cls/exported/best_browser.onnx';
const MODEL_SIZE_BYTES = 5.6 * 1024 * 1024;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;
const TARGET_SIZE = 224;

let session: ort.InferenceSession | null = null;
let modelReady = false;
let fallbackMode = false;
let modelArrayBuffer: ArrayBuffer | null = null;

let resizeCanvas: HTMLCanvasElement | null = null;
let resizeCtx: CanvasRenderingContext2D | null = null;
let inputCanvas: HTMLCanvasElement | null = null;
let inputCtx: CanvasRenderingContext2D | null = null;

let onLoadProgressCallback: ((progress: ProgressInfo) => void) | null = null;

function initResizeCanvas(): void {
  if (!resizeCanvas) {
    resizeCanvas = document.createElement('canvas');
    resizeCanvas.width = TARGET_SIZE;
    resizeCanvas.height = TARGET_SIZE;
    resizeCtx = resizeCanvas.getContext('2d', { willReadFrequently: true });
  }
  if (!inputCanvas) {
    inputCanvas = document.createElement('canvas');
    inputCtx = inputCanvas.getContext('2d');
  }
}

function reportProgress(loaded: number, total: number, stage: string): void {
  if (onLoadProgressCallback) {
    onLoadProgressCallback({
      loaded,
      total: total || MODEL_SIZE_BYTES,
      percent: Math.round((loaded / (total || MODEL_SIZE_BYTES)) * 100),
      stage: stage || 'downloading'
    });
  }
}

function downloadModelWithProgress(): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', MODEL_URL, true);
    xhr.responseType = 'arraybuffer';

    xhr.onprogress = (event) => {
      if (event.lengthComputable) {
        reportProgress(event.loaded, event.total, 'downloading');
      } else {
        reportProgress(event.loaded, MODEL_SIZE_BYTES, 'downloading');
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        modelArrayBuffer = xhr.response as ArrayBuffer;
        reportProgress(modelArrayBuffer.byteLength, modelArrayBuffer.byteLength, 'downloaded');
        resolve(modelArrayBuffer);
      } else {
        reject(new Error('HTTP ' + xhr.status));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error'));
    };

    xhr.send();
  });
}

function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function getExecutionProviders(): string[] {
  const providers: string[] = [];
  if (typeof navigator !== 'undefined' && (navigator as Navigator & { gpu?: unknown }).gpu) {
    providers.push('webgpu');
  }
  if (!isMobileDevice()) {
    providers.push('webgl');
  }
  providers.push('wasm');
  return providers;
}

export function loadModel(attempt?: number, options?: LoadOptions): Promise<ort.InferenceSession | null> {
  attempt = attempt ?? 1;
  options = options ?? {};

  if (options.onProgress) {
    onLoadProgressCallback = options.onProgress;
  }

  const providers = getExecutionProviders();
  console.log('[onnxInference] Using execution providers:', providers);

  const createSession = (buffer: ArrayBuffer): Promise<ort.InferenceSession> => {
    reportProgress(buffer.byteLength, buffer.byteLength, 'initializing');
    return ort.InferenceSession.create(buffer, {
      executionProviders: providers
    });
  };

  const loadPromise: Promise<ArrayBuffer> = modelArrayBuffer
    ? Promise.resolve(modelArrayBuffer)
    : downloadModelWithProgress();

  return loadPromise.then(createSession).then((s) => {
    session = s;
    modelReady = true;
    reportProgress(MODEL_SIZE_BYTES, MODEL_SIZE_BYTES, 'ready');
    console.log('[onnxInference] Model loaded, output names:', session.outputNames);
    return session;
  }).catch((err: Error) => {
    if (attempt! < MAX_RETRIES) {
      console.warn('[onnxInference] Load failed, retry ' + attempt + '/' + MAX_RETRIES, err.message);
      return new Promise<void>((resolve) => {
        setTimeout(resolve, RETRY_DELAY_MS * attempt!);
      }).then(() => {
        return loadModel(attempt! + 1, options);
      });
    }
    console.warn('[onnxInference] Client model failed after retries, using /api/live-predict fallback');
    fallbackMode = true;
    modelReady = true;
    session = null;
    reportProgress(0, 0, 'fallback');
    return null;
  });
}

export function preloadModel(): Promise<ArrayBuffer> {
  if (modelArrayBuffer) {
    return Promise.resolve(modelArrayBuffer);
  }
  return downloadModelWithProgress();
}

function preprocessFrame(imageData: ImageData): ort.Tensor {
  initResizeCanvas();

  inputCanvas!.width = imageData.width;
  inputCanvas!.height = imageData.height;
  inputCtx!.putImageData(imageData, 0, 0);

  resizeCtx!.clearRect(0, 0, TARGET_SIZE, TARGET_SIZE);
  resizeCtx!.drawImage(inputCanvas!, 0, 0, TARGET_SIZE, TARGET_SIZE);
  const resized = resizeCtx!.getImageData(0, 0, TARGET_SIZE, TARGET_SIZE);
  const data = resized.data;

  const tensor = new ort.Tensor('float32',
    new Float32Array(1 * 3 * TARGET_SIZE * TARGET_SIZE),
    [1, 3, TARGET_SIZE, TARGET_SIZE]
  );

  const floatData = tensor.data as Float32Array;
  const pixelCount = TARGET_SIZE * TARGET_SIZE;
  for (let y = 0; y < TARGET_SIZE; y++) {
    for (let x = 0; x < TARGET_SIZE; x++) {
      const i = (y * TARGET_SIZE + x) * 4;
      const px = y * TARGET_SIZE + x;
      floatData[px] = (data[i] ?? 0) / 255.0;
      floatData[px + pixelCount] = (data[i + 1] ?? 0) / 255.0;
      floatData[px + pixelCount * 2] = (data[i + 2] ?? 0) / 255.0;
    }
  }

  return tensor;
}

function runServerFallback(imageData: ImageData): Promise<InferenceResult> {
  initResizeCanvas();
  inputCanvas!.width = imageData.width;
  inputCanvas!.height = imageData.height;
  inputCtx!.putImageData(imageData, 0, 0);
  const dataUrl = inputCanvas!.toDataURL('image/jpeg', 0.8);
  const base64 = dataUrl.split(',')[1];

  return fetch('/api/live-predict', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image: base64 })
  })
  .then((res) => res.json())
  .then((data: Record<string, unknown>) => {
    const rawClass = (data['category'] ?? data['class'] ?? data['class_name'] ?? null) as string | null;
    const confidence = (data['confidence'] ?? data['score'] ?? data['prob'] ?? 0) as number;
    const isBackground = rawClass === 'outro';
    const className = (!isBackground && rawClass && data['success'] !== false) ? rawClass as ClassName : null;
    const allScores = (data['all_scores'] ?? data['scores'] ?? {}) as Partial<Record<ClassName, number>>;
    return { className, confidence, allScores };
  })
  .catch((err: Error) => {
    console.error('[onnxInference] Server fallback failed:', err.message);
    return { className: null, confidence: 0, allScores: {} };
  });
}

export function classify(imageData: ImageData): Promise<InferenceResult> {
  if (!modelReady) {
    return Promise.resolve({ className: null, confidence: 0, allScores: {} });
  }

  if (fallbackMode) {
    return runServerFallback(imageData);
  }

  const tensor = preprocessFrame(imageData);
  const inputName = session!.inputNames[0] ?? '';
  const feed: Record<string, ort.Tensor> = {};
  feed[inputName] = tensor;

  return session!.run(feed).then((results) => {
    const outputName = session!.outputNames[0] ?? '';
    const output = results[outputName];
    if (!output) return { className: null, confidence: 0, allScores: {} };
    const logits = output.data as Float32Array;

    let maxLogit = -Infinity;
    for (let i = 0; i < logits.length; i++) {
      const v = logits[i] ?? -Infinity;
      if (v > maxLogit) maxLogit = v;
    }
    let expSum = 0;
    const probs = new Float32Array(logits.length);
    for (let i = 0; i < logits.length; i++) {
      const e = Math.exp((logits[i] ?? 0) - maxLogit);
      probs[i] = e;
      expSum += e;
    }
    for (let i = 0; i < probs.length; i++) {
      probs[i] = (probs[i] ?? 0) / expSum;
    }

    let maxIdx = 0;
    let maxProb = 0;
    for (let i = 0; i < probs.length; i++) {
      const p = probs[i] ?? 0;
      if (p > maxProb) { maxProb = p; maxIdx = i; }
    }

    const sorted = Array.from(probs).sort((a, b) => b - a);
    const dominance = (sorted[0] ?? 0) / ((sorted[1] ?? 0) + 1e-6);
    const rawClass = CLASSES[maxIdx] ?? 'outro';
    const isBackground = rawClass === 'outro';
    const ar = brandStore.arConfig;
    const minConf = ar?.minValidConfidence ?? 0.3;
    const domRatio = ar?.dominanceRatio ?? 1.5;
    const isValid = !isBackground && maxProb >= minConf && dominance >= domRatio;
    const className = isValid ? rawClass : null;
    const confidence = maxProb;

    const allScores: Partial<Record<ClassName, number>> = {};
    for (let j = 0; j < probs.length && j < CLASSES.length; j++) {
      const cls = CLASSES[j];
      if (cls !== undefined) allScores[cls] = probs[j] ?? 0;
    }

    return { className, confidence, allScores };
  }).catch((err: Error) => {
    console.error('[onnxInference] Inference error, switching to fallback:', err.message);
    fallbackMode = true;
    return runServerFallback(imageData);
  });
}

export function createDebouncedClassifier(
  onStabilized: (result: InferenceResult) => void,
  onFrame?: (result: InferenceResult) => void
): DebouncedClassifier {
  let lastClass: ClassName | null = null;
  let consecutiveCount = 0;

  function processDebounce(result: InferenceResult): void {
    if (!result.className) {
      consecutiveCount = 0;
      lastClass = null;
      return;
    }

    const ar = brandStore.arConfig;
    const debounceConf = ar?.debounceConfidenceThreshold ?? 0.6;
    const debounceFrames = ar?.debounceFrames ?? 3;

    if (result.className === lastClass && result.confidence > debounceConf) {
      consecutiveCount++;
      if (consecutiveCount >= debounceFrames) {
        onStabilized({
          className: result.className,
          confidence: result.confidence,
          allScores: result.allScores
        });
        consecutiveCount = 1;
      }
    } else if (result.confidence > debounceConf) {
      lastClass = result.className;
      consecutiveCount = 1;
    } else {
      consecutiveCount = 0;
      lastClass = null;
    }
  }

  return {
    processFrame(imageData: ImageData): void {
      classify(imageData).then((result) => {
        if (onFrame) onFrame(result);
        processDebounce(result);
      }).catch((err: Error) => {
        console.warn('[onnxInference] Inference error:', err);
      });
    },
    reset(): void {
      lastClass = null;
      consecutiveCount = 0;
    }
  };
}

export interface ZoneDetectionResult {
  zoneIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
  className: ClassName;
  confidence: number;
}

const ZONE_THRESHOLD = 0.40;
const ZONE_DOMINANCE = 1.5;
const ZONE_MIN_CONFIDENCE = 0.30;
const GRID = 3;

let zoneCanvas: HTMLCanvasElement | null = null;
let zoneCtx: CanvasRenderingContext2D | null = null;

function initZoneCanvas(): void {
  if (!zoneCanvas) {
    zoneCanvas = document.createElement('canvas');
    zoneCtx = zoneCanvas.getContext('2d', { willReadFrequently: true });
  }
}

function extractRegionImageData(imageData: ImageData, x: number, y: number, w: number, h: number): ImageData {
  initZoneCanvas();
  zoneCanvas!.width = w;
  zoneCanvas!.height = h;
  zoneCtx!.putImageData(imageData, -x, -y);
  return zoneCtx!.getImageData(0, 0, w, h);
}

export async function classifyAllZones(imageData: ImageData): Promise<ZoneDetectionResult[]> {
  if (!modelReady) return [];

  const w = imageData.width;
  const h = imageData.height;
  const cellW = Math.floor(w / GRID);
  const cellH = Math.floor(h / GRID);

  const tasks: Array<{ zoneIndex: number; x: number; y: number; w: number; h: number; region: ImageData }> = [];
  for (let row = 0; row < GRID; row++) {
    for (let col = 0; col < GRID; col++) {
      const x = col * cellW;
      const y = row * cellH;
      const zoneW = col === GRID - 1 ? w - x : cellW;
      const zoneH = row === GRID - 1 ? h - y : cellH;
      const region = extractRegionImageData(imageData, x, y, zoneW, zoneH);
      tasks.push({ zoneIndex: row * GRID + col, x, y, w: zoneW, h: zoneH, region });
    }
  }

  const results = await Promise.all(
    tasks.map(async (task) => ({ task, result: await classify(task.region) }))
  );

  const detections: ZoneDetectionResult[] = [];
  for (const { task, result } of results) {
    if (!result.className) continue;
    if (result.className === 'outro' && result.confidence < ZONE_MIN_CONFIDENCE) continue;
    if (result.confidence < ZONE_THRESHOLD) continue;
    const sorted = Object.values(result.allScores).sort((a, b) => b - a);
    const dominance = (sorted[0] ?? 0) / ((sorted[1] ?? 0) + 1e-6);
    if (dominance < ZONE_DOMINANCE) continue;
    detections.push({
      zoneIndex: task.zoneIndex,
      x: task.x, y: task.y, w: task.w, h: task.h,
      className: result.className,
      confidence: result.confidence,
    });
  }

  return detections;
}

export function releaseModel(): void {
  if (session && typeof session.release === 'function') {
    session.release();
    session = null;
  }
  modelReady = false;
  fallbackMode = false;
}

export function isReady(): boolean {
  return modelReady;
}

export function isUsingFallback(): boolean {
  return fallbackMode;
}

export interface FrameDetection {
  frameIndex: number;
  time: number;
  className: ClassName | null;
  confidence: number;
  allScores: Partial<Record<ClassName, number>>;
  thumbnailDataUrl: string;
}

export async function classifyFrames(
  frames: ImageData[],
  times: number[],
  thumbnailDataUrls: string[],
  onProgress?: (done: number, total: number) => void
): Promise<FrameDetection[]> {
  const total = frames.length;
  const results: FrameDetection[] = [];
  for (let i = 0; i < total; i++) {
    const result = await classify(frames[i]!);
    results.push({
      frameIndex: i,
      time: times[i] ?? 0,
      className: result.className,
      confidence: result.confidence,
      allScores: result.allScores,
      thumbnailDataUrl: thumbnailDataUrls[i] ?? '',
    });
    if (onProgress) onProgress(i + 1, total);
  }
  return results;
}

export function filterInterestingFrames(
  detections: FrameDetection[],
  minConfidence = 0.35
): FrameDetection[] {
  return detections.filter((d) => {
    if (!d.className) return false;
    if (d.className === 'outro') return false;
    if (d.confidence < minConfidence) return false;
    return true;
  });
}
